import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router'

import { api, can, isActive, type BucketDetail, type BucketPatch, type Effective, type RunDetail, type SpecReport } from '../api'
import { ErrorBox, FindingsList, Pill, RunStatus } from '../components'
import { bytes, dt, duration, elapsed, num, yesNo } from '../format'
import { gridTheme } from '../grid'
import { MappingEditor, type BuildSpecDraft } from './MappingEditor'
import { pollWhile, useNow } from '../hooks'
import { HistoryLink } from './history'

export function BucketDetailPage() {
  const { slug = '' } = useParams()
  const qc = useQueryClient()
  const now = useNow(1000)
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const q = useQuery({
    queryKey: ['bucket', slug],
    queryFn: () => api.bucket(slug),
    refetchInterval: (query) => pollWhile(!!query.state.data?.runs.some(isActive)),
  })
  const sample = useQuery({ queryKey: ['sample', slug, q.data?.last_success_run_id], queryFn: () => api.sample(slug, 50), enabled: !!q.data })
  const [editing, setEditing] = useState(false)

  const onSaved = (d: BucketDetail) => {
    qc.setQueryData(['bucket', slug], d)
    qc.invalidateQueries({ queryKey: ['buckets'] })
  }
  const patch = useMutation({ mutationFn: (p: BucketPatch) => api.patchBucket(slug, p), onSuccess: onSaved })
  const build = useMutation({
    mutationFn: () => api.triggerBuild(slug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bucket', slug] })
      qc.invalidateQueries({ queryKey: ['buckets'] })
    },
  })

  if (q.error) return <p className="callout err">{q.error.message}</p>
  if (!q.data) return <p className="muted">Loading…</p>
  const b = q.data
  const editor = can(me.data, 'editor')
  const active = b.runs.find(isActive)
  const last = b.builds[0]

  return (
    <section className="page">
      <p className="crumbs">
        <Link to="/buckets">Buckets</Link> / <span className="mono">{b.slug}</span>
      </p>
      <header className="page-h">
        <div>
          <h1>{b.title}</h1>
          <p className="sub">{b.description}</p>
        </div>
        <div className="row">
          <HistoryLink kind="bucket" slug={b.slug} />
          {editor && (
            <button
              className="btn btn-primary"
              disabled={build.isPending || !!active || b.spec_version === 0}
              title={active ? `Run #${active.id} is ${active.status}` : 'Queue a build now, even if the inputs have not changed'}
              onClick={() => build.mutate()}
            >
              {active ? 'Building…' : 'Build now'}
            </button>
          )}
          {b.enabled ? <Pill kind="ok">builds on</Pill> : <Pill kind="warn">paused</Pill>}
          {editor && (
            <button className="btn" disabled={patch.isPending} onClick={() => patch.mutate({ enabled: !b.enabled })}>
              {b.enabled ? 'Pause' : 'Resume'}
            </button>
          )}
        </div>
      </header>
      {patch.error && <ErrorBox error={patch.error} />}
      {build.error && <ErrorBox error={build.error} />}
      {active && (
        <div className="progress" role="status">
          <span className="dot" aria-hidden />
          <span>
            <Link to={`/runs/${active.id}`}>Run #{active.id}</Link> <RunStatus run={active} link={false} />
          </span>
          <span className="muted">{active.status === 'running' ? elapsed(active.started_at, null, now) : 'waiting for the worker'}</span>
        </div>
      )}

      <div className="cols-2">
        <div className="card">
          <div className="row-between">
            <h2>Bucket</h2>
            {editor && !editing && (
              <button className="btn btn-sm" onClick={() => setEditing(true)}>
                Edit
              </button>
            )}
          </div>
          {editing ? (
            <SettingsForm b={b} pending={patch.isPending} onCancel={() => setEditing(false)}
              onSave={(p) => patch.mutate(p, { onSuccess: () => setEditing(false) })} />
          ) : (
            <dl className="kv">
              <dt>Rows</dt>
              <dd className="mono">{num(last?.rows)}</dd>
              <dt>Data from</dt>
              <dd>{dt(last?.data_fetched_at)}</dd>
              <dt>Last built</dt>
              <dd>{last ? <>{dt(last.built_at)} <Link to={`/runs/${last.run_id}`} className="mono">#{last.run_id}</Link></> : 'never'}</dd>
              <dt>Storage</dt>
              <dd className="mono">
                {b.storage}
                {last?.serve_table && <span className="muted"> · {last.serve_table}</span>}
              </dd>
              <dt>Cache TTL</dt>
              <dd>{duration(b.ttl_seconds)}</dd>
              <dt>Exportable</dt>
              <dd>{b.exportable ? 'yes (bulk endpoints allowed if the licence permits)' : 'no'}</dd>
              <dt>Schedule</dt>
              <dd className="mono">{b.schedule ?? <span className="muted">only when inputs change</span>}</dd>
              <dt>Inputs</dt>
              <dd className="mono">{b.inputs.join(', ') || '–'}</dd>
              <dt>Used by</dt>
              <dd className="mono">{b.dependents.length ? b.dependents.map((d) => <Link key={d} to={`/buckets/${d}`}>{d} </Link>) : '–'}</dd>
            </dl>
          )}
        </div>
        <LicenseCard license={b.license} through={b.through_buckets} findings={b.findings} />
      </div>

      <div className="card">
        <div className="row-between">
          <h2>Sample</h2>
          {sample.data?.table && (
            <span className="muted mono">
              {sample.data.table} · ~{num(sample.data.total)} rows
            </span>
          )}
        </div>
        {sample.error && <p className="callout err">{sample.error.message}</p>}
        {sample.data && !sample.data.table && <p className="muted">Not loaded into Postgres yet (never built, or storage is Parquet only).</p>}
        {sample.data?.table && <SampleGrid rows={sample.data.rows} columns={b.columns.map((c) => c.name)} />}
      </div>

      <div className="cols-2">
        <div className="card">
          <h2>Builds</h2>
          {b.builds.length === 0 ? (
            <p className="muted">No successful build yet.</p>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Run</th><th>Built</th><th className="num">Rows</th><th className="num">Quarantined</th><th className="num">Size</th><th>Spec</th>
                </tr>
              </thead>
              <tbody>
                {b.builds.map((x) => (
                  <tr key={x.run_id}>
                    <td><Link to={`/runs/${x.run_id}`} className="mono">#{x.run_id}</Link></td>
                    <td>{dt(x.built_at)}</td>
                    <td className="num">{num(x.rows)}</td>
                    <td className="num">{x.quarantined ? <span className="err-t">{num(x.quarantined)}</span> : 0}</td>
                    <td className="num">{x.output_deleted_at ? <span className="muted">deleted</span> : bytes(x.bytes)}</td>
                    <td className="mono">v{x.spec_version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="card">
          <h2>Quarantine (last run)</h2>
          {b.quarantine.length === 0 ? (
            <p className="callout ok">No rows quarantined.</p>
          ) : (
            <table className="tbl">
              <thead>
                <tr><th>Rule</th><th>Reason</th><th className="num">Rows</th></tr>
              </thead>
              <tbody>
                {b.quarantine.map((r) => (
                  <tr key={r.rule}>
                    <td className="mono">{r.rule}</td>
                    <td>{r.reason}</td>
                    <td className="num">{num(r.rows)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {b.last_run_id && b.quarantine.length > 0 && (
            <p className="note">
              Rows are on <Link to={`/runs/${b.last_run_id}`}>run #{b.last_run_id}</Link> (first 1000 here, all of them in the run's quarantine Parquet).
            </p>
          )}
        </div>
      </div>

      <div className="cols-2">
        <div className="card">
          <h2>Columns</h2>
          <table className="tbl">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Description</th></tr>
            </thead>
            <tbody>
              {b.columns.map((c) => (
                <tr key={c.name}>
                  <td className="mono">{c.name}</td>
                  <td className="mono muted">{c.type}</td>
                  <td>{c.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card">
          <h2>Lineage <span className="muted mono">spec v{b.spec_version}</span></h2>
          <table className="tbl">
            <thead>
              <tr><th>Field</th><th>From</th></tr>
            </thead>
            <tbody>
              {b.lineage.map((l, i) => (
                <tr key={i}>
                  <td className="mono">{l.field === '*' ? <span className="muted">join / filter</span> : l.field}</td>
                  <td className="mono">
                    {l.input_kind === 'bucket' ? <Link to={`/buckets/${l.input_slug}`}>{l.input_slug}</Link> : l.input_slug}.{l.input_field}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SpecEditor key={b.spec_version} b={b} editor={editor} onSaved={onSaved} />

      <div className="card">
        <h2>Runs</h2>
        <table className="tbl">
          <thead>
            <tr>
              <th>Run</th><th>Status</th><th>Trigger</th><th>Created</th><th>Duration</th><th className="num">In</th><th className="num">Out</th>
              <th className="num">Quarantined</th><th>Error</th>
            </tr>
          </thead>
          <tbody>
            {b.runs.map((r) => (
              <tr key={r.id}>
                <td><Link to={`/runs/${r.id}`} className="mono">#{r.id}</Link></td>
                <td><RunStatus run={r} link={false} /></td>
                <td>{r.trigger}</td>
                <td>{dt(r.created_at)}</td>
                <td>{elapsed(r.started_at, r.finished_at, now)}</td>
                <td className="num">{num(r.rows_in)}</td>
                <td className="num">{num(r.rows_out)}</td>
                <td className="num">{num(r.quarantined)}</td>
                <td className="err-t">{r.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function LicenseCard({ license, through, findings }: { license: Effective | null; through: string[]; findings: BucketDetail['findings'] }) {
  return (
    <div className="card">
      <h2>Effective licence</h2>
      {!license ? (
        <p className="muted">No spec, so no inputs.</p>
      ) : (
        <>
          <dl className="kv">
            <dt>Licences</dt>
            <dd>{license.licenses.join(' + ') || '–'}</dd>
            <dt>Usage mode</dt>
            <dd className="mono">{license.usage_mode}</dd>
            <dt>Commercial use</dt>
            <dd>{yesNo(license.commercial_use)}</dd>
            <dt>Redistribution</dt>
            <dd>{yesNo(license.redistribution)}</dd>
            <dt>Share-alike</dt>
            <dd>{license.share_alike ? 'yes' : 'no'}</dd>
            <dt>Caching limit</dt>
            <dd>{duration(license.caching_limit_seconds)}</dd>
            <dt>Sources</dt>
            <dd>
              {license.sources.map((s) => (
                <Link key={s} to={`/sources/${s}`} className="mono">
                  {s}{' '}
                </Link>
              ))}
              {through.length > 0 && <span className="muted"> via {through.join(', ')}</span>}
            </dd>
            {license.undisclosed?.length > 0 && (
              <>
                <dt>Not mentioned</dt>
                <dd>
                  {license.undisclosed.map((s) => (
                    <Link key={s} to={`/sources/${s}`} className="mono">
                      {s}{' '}
                    </Link>
                  ))}
                  <span className="muted"> (published without attribution; still restricts the licence)</span>
                </dd>
              </>
            )}
          </dl>
          {license.attribution.length > 0 && (
            <figure className="attribution">
              <figcaption>Attribution</figcaption>
              {license.attribution.map((a) => (
                <blockquote key={a}>{a}</blockquote>
              ))}
            </figure>
          )}
          <FindingsList findings={findings} empty="Every upstream licence is complete." />
        </>
      )}
    </div>
  )
}

function SampleGrid({ rows, columns }: { rows: Record<string, unknown>[]; columns: string[] }) {
  const cols = useMemo<ColDef<Record<string, unknown>>[]>(
    () =>
      columns.map((c) => ({
        field: c,
        minWidth: 110,
        flex: 1,
        valueFormatter: (p) => (p.value === null || p.value === undefined ? '–' : typeof p.value === 'object' ? JSON.stringify(p.value) : String(p.value)),
        tooltipValueGetter: (p) => (typeof p.value === 'object' ? JSON.stringify(p.value) : String(p.value ?? '')),
      })),
    [columns],
  )
  return (
    <div className="grid-wrap" style={{ height: 360 }}>
      <AgGridReact theme={gridTheme} rowData={rows} columnDefs={cols} tooltipShowDelay={300} defaultColDef={{ sortable: true, resizable: true }} />
    </div>
  )
}

function SettingsForm({ b, pending, onSave, onCancel }: { b: BucketDetail; pending: boolean; onSave: (p: BucketPatch) => void; onCancel: () => void }) {
  const [f, setF] = useState({
    title: b.title,
    description: b.description,
    storage: b.storage,
    exportable: b.exportable,
    schedule: b.schedule ?? '',
    ttl_seconds: String(b.ttl_seconds),
  })
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF({ ...f, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value })
  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault()
        onSave({ ...f, schedule: f.schedule.trim() || null, ttl_seconds: Number(f.ttl_seconds) })
      }}
    >
      <div className="form-grid">
        <label className="wide">
          Title
          <input value={f.title} onChange={set('title')} />
        </label>
        <label className="wide">
          Description
          <textarea rows={2} value={f.description} onChange={set('description')} />
        </label>
        <label>
          Storage
          <select value={f.storage} onChange={set('storage')}>
            <option value="both">both (Parquet + Postgres)</option>
            <option value="postgres">postgres</option>
            <option value="parquet">parquet only</option>
          </select>
        </label>
        <label>
          Cache TTL (seconds)
          <input type="number" min={0} value={f.ttl_seconds} onChange={set('ttl_seconds')} />
        </label>
        <label>
          Schedule (cron, optional)
          <input className="mono" placeholder="e.g. 0 4 * * *" value={f.schedule} onChange={set('schedule')} />
        </label>
        <label className="check">
          <input type="checkbox" checked={f.exportable} onChange={set('exportable')} /> Exportable (bulk endpoints)
        </label>
      </div>
      <div className="row">
        <button className="btn btn-primary" disabled={pending}>
          Save
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

/** The build spec as JSON: view any version, validate a draft, save it as the next version. */
function SpecEditor({ b, editor, onSaved }: { b: BucketDetail; editor: boolean; onSaved: (d: BucketDetail) => void }) {
  const [version, setVersion] = useState(b.spec?.version ?? 0)
  const shown = b.versions.find((v) => v.version === version) ?? b.spec
  const [text, setText] = useState(() => JSON.stringify(b.spec?.body ?? {}, null, 2))
  const [comment, setComment] = useState('')
  const [report, setReport] = useState<SpecReport | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [view, setView] = useState<'mapping' | 'json'>('mapping')
  let draft: BuildSpecDraft | null = null
  try {
    draft = JSON.parse(text) as BuildSpecDraft
  } catch {
    draft = null
  }

  const parsed = (): unknown | undefined => {
    try {
      setParseError(null)
      return JSON.parse(text)
    } catch (e) {
      setParseError((e as Error).message)
      return undefined
    }
  }
  const validate = useMutation({ mutationFn: (spec: unknown) => api.validateSpec(b.slug, spec), onSuccess: setReport })
  const save = useMutation({ mutationFn: (spec: unknown) => api.saveSpec(b.slug, spec, comment), onSuccess: onSaved })
  const preview = useMutation({ mutationFn: (spec: unknown) => api.previewSpec(b.slug, spec) })
  const previewRun = useQuery({
    queryKey: ['run', preview.data?.id],
    queryFn: () => api.run(preview.data!.id),
    enabled: !!preview.data,
    refetchInterval: (q) => (q.state.data && !isActive(q.state.data) ? false : 1500),
  })
  const dirty = text !== JSON.stringify(b.spec?.body ?? {}, null, 2)

  return (
    <div className="card">
      <div className="row-between">
        <h2>Build spec</h2>
        <div className="row">
          <select aria-label="Version" value={version} onChange={(e) => setVersion(Number(e.target.value))}>
            {b.versions.map((v) => (
              <option key={v.version} value={v.version}>
                v{v.version} · {dt(v.created_at)} · {v.created_by}
                {v.comment ? ` · ${v.comment}` : ''}
              </option>
            ))}
          </select>
          {shown && version !== b.spec?.version && (
            <button className="btn btn-sm" onClick={() => setText(JSON.stringify(shown.body, null, 2))}>
              Load v{version} into the editor
            </button>
          )}
        </div>
      </div>
      <p className="note">
        Versions are immutable: saving creates v{b.spec_version + 1}. Builds use the latest saved version. Expressions are CEL, checked
        against the inputs as you type and compiled to SQL by the platform (no SQL statements, files or network).
      </p>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={view === 'mapping'} className={view === 'mapping' ? 'on' : ''} onClick={() => setView('mapping')}>Mapping</button>
        <button role="tab" aria-selected={view === 'json'} className={view === 'json' ? 'on' : ''} onClick={() => setView('json')}>JSON</button>
      </div>
      {version !== b.spec?.version && shown ? (
        <code className="block spec">{JSON.stringify(shown.body, null, 2)}</code>
      ) : view === 'mapping' ? (
        draft ? (
          <MappingEditor slug={b.slug} spec={draft} readOnly={!editor} onChange={(d) => {
            setText(JSON.stringify(d, null, 2))
            setReport(null)
          }} />
        ) : (
          <p className="callout err">The JSON does not parse; fix it in the JSON tab.</p>
        )
      ) : (
        <textarea className="spec mono" spellCheck={false} rows={22} value={text} readOnly={!editor}
          onChange={(e) => {
            setText(e.target.value)
            setReport(null)
          }} />
      )}
      {parseError && <p className="callout err">Not valid JSON: {parseError}</p>}
      <div className="row">
        <button className="btn" disabled={validate.isPending} onClick={() => {
          const s = parsed()
          if (s !== undefined) validate.mutate(s)
        }}>
          {validate.isPending ? 'Checking…' : 'Validate'}
        </button>
        {editor && (
          <button className="btn" disabled={preview.isPending || isActive(previewRun.data)} title="Dry-run build of this draft on the current inputs (nothing is saved)"
            onClick={() => {
              const s = parsed()
              if (s !== undefined) preview.mutate(s)
            }}>
            {preview.isPending || isActive(previewRun.data) ? 'Previewing…' : 'Preview output'}
          </button>
        )}
        {editor && (
          <>
            <input className="grow" placeholder="What changed (saved with the version)" value={comment} onChange={(e) => setComment(e.target.value)} />
            <button className="btn btn-primary" disabled={!dirty || save.isPending} onClick={() => {
              const s = parsed()
              if (s !== undefined) save.mutate(s)
            }}>
              {save.isPending ? 'Saving…' : `Save as v${b.spec_version + 1}`}
            </button>
          </>
        )}
      </div>
      {validate.error && <ErrorBox error={validate.error} />}
      {save.error && <ErrorBox error={save.error} />}
      {report && <ReportView report={report} />}
      {preview.error && <ErrorBox error={preview.error} />}
      {previewRun.data && <PreviewView run={previewRun.data} />}
    </div>
  )
}

function PreviewView({ run }: { run: RunDetail }) {
  if (isActive(run)) {
    return (
      <p className="progress" role="status">
        <span className="dot" aria-hidden /> Preview run <Link to={`/runs/${run.id}`}>#{run.id}</Link> <RunStatus run={run} link={false} />
      </p>
    )
  }
  if (run.status !== 'succeeded' || !run.result) {
    return <p className="callout err">Preview failed: {run.error ?? run.status} (<Link to={`/runs/${run.id}`}>run #{run.id}</Link>)</p>
  }
  const r = run.result
  return (
    <div className="report">
      <p className="callout ok">
        Preview: {num(r.rows_in)} rows in scope, {num(r.final)} out, {num(r.quarantined)} quarantined
        {Object.keys(r.by_rule).length > 0 && ` (${Object.entries(r.by_rule).map(([k, v]) => `${k}: ${num(v)}`).join(', ')})`}. Nothing was saved.
      </p>
      {r.note && <p className="note">{r.note}</p>}
      {r.rows.length > 0 && <SampleGrid rows={r.rows} columns={r.columns} />}
      {r.quarantine.length > 0 && (
        <>
          <h3 className="h3">Quarantined (first {r.quarantine.length})</h3>
          <SampleGrid rows={r.quarantine} columns={['__rule', ...r.columns]} />
        </>
      )}
    </div>
  )
}

function ReportView({ report }: { report: SpecReport }) {
  return (
    <div className="report">
      {report.problems.length > 0 && (
        <ul className="findings">
          {report.problems.map((p, i) => (
            <li key={i} className="callout err">
              <strong>Problem</strong> {p}
            </li>
          ))}
        </ul>
      )}
      <FindingsList findings={report.findings} />
      {report.ok && <p className="callout ok">The spec compiles and runs against empty inputs. Saving it is allowed.</p>}
      {report.sql.length > 0 && (
        <details>
          <summary className="muted">Compiled SQL ({report.sql.length} statements)</summary>
          {report.sql.map((s, i) => (
            <code key={i} className="block">
              {s}
            </code>
          ))}
        </details>
      )}
    </div>
  )
}
